/**
 * Assumption acknowledgment form (S4d, P0-4).
 *
 * WHAT THIS REPLACED. The form arrived with every field already filled in — 60
 * datasets, a $750/day labour rate, 300 searches a year — drawn from either
 * client constants or a saved "org default". Those figures looked exactly like
 * answers the user had given. The server then held its own identical defaults, so
 * a plan generated by pressing "Generate" without touching anything was priced
 * against numbers nobody had ever supplied, and the evidence chain described them
 * as "User-provided organization context".
 *
 * "Skip — Use Defaults" made it worse by naming the behaviour: it promised
 * defaults and delivered a fully-costed plan indistinguishable from one built on
 * real inputs.
 *
 * WHAT THIS DOES INSTEAD.
 *   - Nothing is accepted until the user acts. There are no pre-filled answers.
 *   - Reference values are fetched from the server that validates against them,
 *     shown as clearly-marked suggestions with their citation, and applied only
 *     by an explicit per-field "Use reference value" action.
 *   - Typing a value records `user_supplied`; accepting a suggestion records
 *     `reference_default`, and the server re-checks the value against its own
 *     published catalog and version.
 *   - "Continue without these inputs" submits no acknowledgments at all and says
 *     plainly what that costs: value, ROI and payback will read Unavailable.
 *     That is a legitimate choice, not an error.
 *
 * The reference values are NOT duplicated here. A client copy could drift from
 * the catalog the server validates, and then accepting a suggestion would assert
 * a citation for a number the server never published.
 *
 * The saved-organization-context load and "save as my default" write are gone
 * with it: a stored default pre-filled into the form is the prohibited behaviour,
 * and a write with nothing reading it has no purpose.
 *
 * @see server/services/actionPlanAssumptions.ts — the canonical catalog
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useApiClient } from '../hooks/useApiClient';
import {
  ASSUMPTION_KEYS,
  ASSUMPTION_LABEL,
  VALUE_CRITICAL_ASSUMPTIONS,
  type AcknowledgedAssumption,
  type AssumptionKey,
  type ReferenceAssumption,
  type ReferenceAssumptionCatalog,
} from '../types/actionPlanAssumptions';

// ============================================================================
// TYPES
// ============================================================================

interface BusinessConfigFormProps {
  journeyId: string;
  /** Emits the acknowledgments to submit. Never a synthesised default set. */
  onConfigured: (acknowledged: AcknowledgedAssumption[]) => void;
  /** "Continue without these inputs" — submits nothing. */
  onSkip: () => void;
  mode: 'inline' | 'modal';
}

/** One field's state. A null `acknowledged` means the user has not answered it. */
interface FieldState {
  raw: string;
  acknowledged: AcknowledgedAssumption | null;
  error: string | null;
}

type FieldStates = Record<AssumptionKey, FieldState>;

const EMPTY_FIELD: FieldState = { raw: '', acknowledged: null, error: null };

const NUMERIC_KEYS: readonly AssumptionKey[] = ['dataset_count', 'hourly_rate', 'team_size'];

const ORGANIZATION_SIZES = ['small', 'medium', 'large'] as const;

/** Stable DOM ids, so labels and descriptions can reference them. */
const fieldId = (key: AssumptionKey): string => `assumption-${key}`;
const helpId = (key: AssumptionKey): string => `assumption-${key}-help`;
const errorId = (key: AssumptionKey): string => `assumption-${key}-error`;
const referenceId = (key: AssumptionKey): string => `assumption-${key}-reference`;

function initialFields(): FieldStates {
  return ASSUMPTION_KEYS.reduce((acc, key) => {
    acc[key] = { ...EMPTY_FIELD };
    return acc;
  }, {} as FieldStates);
}

/** Validate a typed value. Returns an error message, or null when acceptable. */
function validateTyped(key: AssumptionKey, raw: string): string | null {
  if (raw.trim() === '') return null; // Empty means "not acknowledged", not invalid.

  if (NUMERIC_KEYS.includes(key)) {
    const n = Number(raw);
    if (!Number.isFinite(n)) return 'Enter a number.';
    if (n < 0) return 'Enter a value of zero or more.';
    if (key === 'dataset_count' && !Number.isInteger(n)) {
      return 'Enter a whole number of datasets.';
    }
    if (key === 'team_size' && !Number.isInteger(n)) {
      return 'Enter a whole number of people.';
    }
    return null;
  }
  if (raw.length > 120) return 'Keep this under 120 characters.';
  return null;
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export const BusinessConfigForm: React.FC<BusinessConfigFormProps> = ({
  journeyId: _journeyId,
  onConfigured,
  onSkip,
  mode,
}) => {
  const { authFetch } = useApiClient();

  const [catalog, setCatalog] = useState<ReferenceAssumptionCatalog | null>(null);
  const [catalogError, setCatalogError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  // Starts with NOTHING acknowledged. This is the whole point of the rewrite.
  const [fields, setFields] = useState<FieldStates>(initialFields);

  const loadCatalog = useCallback(async () => {
    try {
      const response = await authFetch('/api/action-plan/reference-assumptions');
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      setCatalog((await response.json()) as ReferenceAssumptionCatalog);
    } catch (err) {
      // A failed fetch must NOT fall back to hardcoded numbers — that is the
      // duplicate catalog this design exists to prevent. The user can still enter
      // their own figures, or continue without any.
      setCatalogError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  useEffect(() => {
    loadCatalog();
  }, [loadCatalog]);

  const referenceFor = useCallback(
    (key: AssumptionKey): ReferenceAssumption | undefined =>
      catalog?.assumptions.find((a) => a.key === key),
    [catalog],
  );

  // ==========================================================================
  // HANDLERS
  // ==========================================================================

  /** Typing records `user_supplied`, or clears the acknowledgment when emptied. */
  const handleType = (key: AssumptionKey, raw: string): void => {
    const error = validateTyped(key, raw);
    setFields((prev) => ({
      ...prev,
      [key]: {
        raw,
        error,
        acknowledged:
          error !== null || raw.trim() === ''
            ? null
            : {
                key,
                value: NUMERIC_KEYS.includes(key) ? Number(raw) : raw.trim(),
                basis: 'user_supplied',
              },
      },
    }));
  };

  /**
   * Accept the cited reference for one field.
   *
   * The submitted value is the catalog's, and `reference_version` records which
   * catalog was on screen — the server rejects a mismatch rather than silently
   * reinterpreting a stale acceptance as a claim about the current reference.
   */
  const handleUseReference = (key: AssumptionKey): void => {
    const ref = referenceFor(key);
    if (!ref) return;
    setFields((prev) => ({
      ...prev,
      [key]: {
        raw: String(ref.value),
        error: null,
        acknowledged: {
          key,
          value: ref.value,
          basis: 'reference_default',
          reference_version: ref.version,
        },
      },
    }));
  };

  const handleClear = (key: AssumptionKey): void => {
    setFields((prev) => ({ ...prev, [key]: { ...EMPTY_FIELD } }));
  };

  const acknowledged = useMemo(
    () =>
      ASSUMPTION_KEYS.map((k) => fields[k].acknowledged).filter(
        (a): a is AcknowledgedAssumption => a !== null,
      ),
    [fields],
  );

  const hasErrors = ASSUMPTION_KEYS.some((k) => fields[k].error !== null);
  const missingCritical = VALUE_CRITICAL_ASSUMPTIONS.filter(
    (k) => fields[k].acknowledged === null,
  );

  // ==========================================================================
  // RENDER
  // ==========================================================================

  if (loading) {
    return (
      <div className="business-config-form loading">
        <div className="spinner-small"></div>
        <span>Loading reference values...</span>
      </div>
    );
  }

  /** The suggestion row for one field: value, citation, and an explicit action. */
  const renderReference = (key: AssumptionKey): React.ReactNode => {
    const ref = referenceFor(key);
    if (!ref) return null;
    const isAccepted = fields[key].acknowledged?.basis === 'reference_default';

    return (
      <div className="assumption-reference" id={referenceId(key)}>
        <p className="assumption-reference__value">
          <span className="assumption-reference__tag">Reference value</span>{' '}
          <strong>
            {ref.value} {ref.unit}
          </strong>
        </p>
        <p className="assumption-reference__basis">{ref.basis}</p>
        <button
          type="button"
          className="btn-link assumption-reference__accept"
          onClick={() => handleUseReference(key)}
          disabled={isAccepted}
        >
          {isAccepted
            ? `Reference value accepted for ${ASSUMPTION_LABEL[key]}`
            : `Use reference value for ${ASSUMPTION_LABEL[key]}`}
        </button>
      </div>
    );
  };

  /** The provenance of a field's current acknowledgment, stated in words. */
  const renderProvenance = (key: AssumptionKey): React.ReactNode => {
    const ack = fields[key].acknowledged;
    if (!ack) {
      return (
        <p className="assumption-provenance assumption-provenance--none">
          Not provided — dependent figures will be unavailable.
        </p>
      );
    }
    return (
      <p className="assumption-provenance">
        {ack.basis === 'user_supplied'
          ? 'Recorded as your own figure.'
          : 'Recorded as an accepted reference value, with its citation.'}{' '}
        <button
          type="button"
          className="btn-link"
          onClick={() => handleClear(key)}
        >
          Clear {ASSUMPTION_LABEL[key]}
        </button>
      </p>
    );
  };

  return (
    <div className={`business-config-form ${mode}`}>
      <div className="form-header">
        <h3>Value calculation inputs</h3>
        <p className="form-subtitle">
          These inputs drive the Pistoia Alliance business value formulas. Nothing is
          assumed on your behalf: anything you leave blank is reported as unavailable
          rather than filled in.
        </p>
      </div>

      {catalogError && (
        <div className="assumption-catalog-error" role="status" aria-live="polite">
          Reference values could not be loaded ({catalogError}). You can still enter your
          own figures, or continue without them — no substitute values are used.
        </div>
      )}

      <div className="form-body">
        {(['dataset_count', 'hourly_rate', 'team_size'] as const).map((key) => (
          <div className="form-group" key={key}>
            <label htmlFor={fieldId(key)}>
              {ASSUMPTION_LABEL[key]}
              {VALUE_CRITICAL_ASSUMPTIONS.includes(key) && (
                <span className="assumption-required-note">
                  {' '}
                  — required for any value figure
                </span>
              )}
            </label>
            <input
              id={fieldId(key)}
              type="number"
              inputMode="numeric"
              // No `value={... || 60}` fallback: an empty field means the user has
              // not answered, and showing a number makes it look answered.
              value={fields[key].raw}
              placeholder="Not provided"
              onChange={(e) => handleType(key, e.target.value)}
              aria-describedby={`${helpId(key)} ${referenceId(key)}`}
              aria-invalid={fields[key].error !== null}
              aria-errormessage={fields[key].error ? errorId(key) : undefined}
              min={0}
            />
            <p className="field-explanation" id={helpId(key)}>
              {key === 'dataset_count' &&
                'Datasets in scope for the FAIR improvements in this plan.'}
              {key === 'hourly_rate' &&
                'Fully-loaded hourly cost of the staff whose time the plan saves.'}
              {key === 'team_size' &&
                'People affected by the improvements. Refines multipliers; not required.'}
            </p>
            {fields[key].error && (
              <p className="field-error" id={errorId(key)} role="alert">
                {fields[key].error}
              </p>
            )}
            {renderReference(key)}
            {renderProvenance(key)}
          </div>
        ))}

        {/* Organization size — a properly named radio group, not bare buttons. */}
        <fieldset className="form-group assumption-fieldset">
          <legend>{ASSUMPTION_LABEL.organization_size}</legend>
          <p className="field-explanation" id={helpId('organization_size')}>
            Affects size-level value multipliers only.
          </p>
          <div className="size-selector">
            {ORGANIZATION_SIZES.map((size) => {
              const id = `${fieldId('organization_size')}-${size}`;
              return (
                <span className="size-option" key={size}>
                  <input
                    type="radio"
                    id={id}
                    name={fieldId('organization_size')}
                    value={size}
                    checked={fields.organization_size.raw === size}
                    onChange={() => handleType('organization_size', size)}
                    aria-describedby={helpId('organization_size')}
                  />
                  <label htmlFor={id}>{size}</label>
                </span>
              );
            })}
          </div>
          {renderReference('organization_size')}
          {renderProvenance('organization_size')}
        </fieldset>

        <div className="form-group">
          <label htmlFor={fieldId('industry')}>{ASSUMPTION_LABEL.industry}</label>
          <input
            id={fieldId('industry')}
            type="text"
            value={fields.industry.raw}
            placeholder="Not provided"
            onChange={(e) => handleType('industry', e.target.value)}
            aria-describedby={`${helpId('industry')} ${referenceId('industry')}`}
            aria-invalid={fields.industry.error !== null}
            aria-errormessage={fields.industry.error ? errorId('industry') : undefined}
            maxLength={120}
          />
          <p className="field-explanation" id={helpId('industry')}>
            Affects segment-level value multipliers only.
          </p>
          {fields.industry.error && (
            <p className="field-error" id={errorId('industry')} role="alert">
              {fields.industry.error}
            </p>
          )}
          {renderReference('industry')}
          {renderProvenance('industry')}
        </div>
      </div>

      {/*
        What is about to be submitted, and what it costs if incomplete. The old
        form's equivalent was a button labelled "Skip - Use Defaults", which
        described the opposite of what actually happened.
      */}
      <div className="assumption-summary" role="status" aria-live="polite">
        {missingCritical.length > 0 ? (
          <p>
            <strong>{missingCritical.map((k) => ASSUMPTION_LABEL[k]).join(' and ')}</strong>{' '}
            {missingCritical.length === 1 ? 'is' : 'are'} not provided, so task values,
            total business value, ROI and payback will be shown as unavailable. Everything
            else in the plan is unaffected.
          </p>
        ) : (
          <p>
            {acknowledged.length} of {ASSUMPTION_KEYS.length} inputs provided. Every figure
            derived from them will carry its source.
          </p>
        )}
      </div>

      <div className="form-actions">
        <button className="btn-secondary" onClick={onSkip} type="button">
          Continue without these inputs
        </button>
        <button
          className="btn-primary"
          onClick={() => onConfigured(acknowledged)}
          disabled={hasErrors}
          type="button"
        >
          Generate Action Plan
        </button>
      </div>
    </div>
  );
};

export default BusinessConfigForm;
